import { Badge } from '@artic/ui/badge';
import { Button } from '@artic/ui/button';
import { useFeatureFlag } from '@artic/ui/feature-flag';
import { Input } from '@artic/ui/input';
import { Label } from '@artic/ui/label';
import { Skeleton } from '@artic/ui/skeleton';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';
import { useMemo } from 'react';

import { usePartnerId } from '../../features/inbox/use-partner-name';
import {
  partnerContainersQueryOptions,
  productListQueryOptions,
} from '../../features/products/queries';
import { filterProducts, productSearchParsers } from '../../features/products/search';

export const Route = createFileRoute('/products/')({
  component: ProductListView,
});

function ProductListView() {
  const planner = useFeatureFlag('planner');

  if (!planner) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Feature Not Available</h2>
        <p className="text-muted-foreground text-sm">
          The Product Catalogue is not enabled for your organisation.
        </p>
      </div>
    );
  }

  return <ProductListContent />;
}

function ProductListContent() {
  const partnerId = usePartnerId();
  const workloadCategoriesEnabled = useFeatureFlag('workload_categories');
  const { data: products, isLoading, isError } = useQuery(productListQueryOptions(partnerId));
  const { data: partnerContainers } = useQuery({
    ...partnerContainersQueryOptions(partnerId),
    enabled: !!partnerId,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const [q, setQ] = useQueryState('q', productSearchParsers.q);

  const filtered = products ? filterProducts(products, q) : [];

  // Containers the partner has flagged as label-containers, ordered by their
  // configured display order so the pills come out in a predictable sequence.
  const labelContainerIds = useMemo(() => {
    const ordered = (partnerContainers ?? [])
      .filter((c) => c.use_for_label)
      .slice()
      .sort((a, b) => a.display_order - b.display_order)
      .map((c) => c.container_id);
    return { ids: new Set(ordered), order: ordered };
  }, [partnerContainers]);

  const showLabelsColumn = labelContainerIds.ids.size > 0;

  if (isError) {
    return (
      <div className="text-destructive py-10 text-center text-sm">
        Failed to load products. Please try again.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Products</h1>
        <Button asChild size="sm">
          <Link to="/products/new">New Product</Link>
        </Button>
      </div>

      <Input
        placeholder="Search products..."
        value={q ?? ''}
        onChange={(e) => void setQ(e.target.value || null)}
        className="max-w-sm"
      />

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-muted-foreground py-10 text-center text-sm">
          {q ? 'No products match your search.' : 'No products yet.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b text-left">
                <th className="px-4 py-2 font-medium">Product Code</th>
                <th className="px-4 py-2 font-medium">Name</th>
                {showLabelsColumn && <th className="px-4 py-2 font-medium">Labels</th>}
                {workloadCategoriesEnabled && (
                  <th className="px-4 py-2 text-right font-medium">Workload Category</th>
                )}
                <th className="px-4 py-2 font-medium">Bill of Operations</th>
                <th className="px-4 py-2 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((product) => {
                // Build pills in the partner's configured container order so
                // products line up visually across rows.
                const selectionsByContainer = new Map(
                  product.container_selections.map((s) => [s.container_id, s]),
                );
                const labelPills = labelContainerIds.order
                  .map((containerId) => selectionsByContainer.get(containerId))
                  .filter((s): s is (typeof product.container_selections)[number] => s != null);

                return (
                  <tr
                    key={product.product_id}
                    className="hover:bg-muted/30 border-b transition-colors"
                  >
                    <td className="px-4 py-2">
                      <Link
                        to="/products/$productId"
                        params={{ productId: product.product_id }}
                        className="text-primary font-mono text-xs font-medium hover:underline"
                      >
                        {product.product_key}
                      </Link>
                    </td>
                    <td className="px-4 py-2">{product.product_name}</td>
                    {showLabelsColumn && (
                      <td className="px-4 py-2">
                        {labelPills.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {labelPills.map((s) => (
                              <Label
                                key={s.container_id}
                                className="bg-secondary text-secondary-foreground rounded-4xl px-2 py-0.5 text-xs whitespace-nowrap"
                                title={s.container_name}
                              >
                                {s.option_name}
                              </Label>
                            ))}
                          </div>
                        )}
                      </td>
                    )}
                    {workloadCategoriesEnabled && (
                      <td className="px-4 py-2 text-right">
                        <Badge
                          variant={
                            product.complexity_score >= 4
                              ? 'destructive'
                              : product.complexity_score >= 3
                                ? 'secondary'
                                : 'default'
                          }
                        >
                          {product.complexity_score}
                        </Badge>
                      </td>
                    )}
                    <td className="px-4 py-2">
                      {product.bill_of_operations_id ? (
                        <Badge variant="outline">Linked</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="text-muted-foreground px-4 py-2 text-xs">
                      {new Date(product.product_created_at).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
